import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Pencil, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { RoleDto } from '../types';

const columnHelper = createColumnHelper<RoleDto>();

function ActionCell({ role }: { role: RoleDto }) {
  const navigate = useNavigate();

  if (role.isSystemRole) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled
              title="System role (protected)"
            >
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>System role — cannot be modified</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => navigate(`/administration/authorization/roles/${role.id}`)}
      title="Edit role"
    >
      <Pencil className="h-4 w-4" />
    </Button>
  );
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <Badge variant={isActive ? 'success' : 'secondary'}>
      {isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
}

export const roleColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('roleCode', {
    header: 'Code',
    cell: (info) => (
      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{info.getValue()}</code>
    ),
  }),
  columnHelper.accessor('description', {
    header: 'Description',
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('permissions', {
    header: 'Permissions',
    cell: (info) => {
      const permissions = info.getValue();
      return (
        <span className="text-muted-foreground">
          {permissions.length} permission{permissions.length !== 1 ? 's' : ''}
        </span>
      );
    },
  }),
  columnHelper.accessor('isActive', {
    header: 'Status',
    cell: (info) => <StatusBadge isActive={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'actions',
    header: '',
    cell: ({ row }) => <ActionCell role={row.original} />,
  }),
];
